import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "../../../../components/common/input";
import { Button } from "../../../../components/common/button";
import { Text } from "../../../../components/common/typography";
import { useFormFields } from "../../../../hooks/useFormFields";
import { useSubmitForm } from "../../../../hooks/useSubmitForm";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { authPasswordService } from "../../../api_client";
import { validateEmail } from "../../../../utils/validation";

type ForgotPwFormValues = { email: string };

export function ForgotPasswordForm() {
  const navigate = useNavigate();
  const [couldNotBeSent, setCouldNotBeSent] = useState(false);

  const { formFields, createChangeHandler } = useFormFields<ForgotPwFormValues>({ email: "" });

  const email = formFields.email.trim();

  const { isLoading, errors, submitHandler, success, clearErrors, setErrors } =
    useSubmitForm<ForgotPwFormValues>({
      submitMethod: async () => authPasswordService.passwordForgot({ email }),
      runAfterError: () => setCouldNotBeSent(true),
      runAfterSuccess: () => {
        setCouldNotBeSent(false);
        navigate(AppRoutes.RESET_PASSWORD_VERIFY, { state: { email } });
      },
    });

  useEffect(() => {
    setCouldNotBeSent(false);
    clearErrors();
  }, [formFields.email, clearErrors]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const problem = validateEmail(email);
    if (problem) {
      setErrors({ email: [problem] });
      return;
    }
    void submitHandler();
  };

  return (
    <form onSubmit={onSubmit} noValidate className="flex w-full flex-col gap-10">
      <Input
        label="Email address"
        type="email"
        name="email"
        placeholder="email@example.com"
        autoComplete="email"
        error={errors?.email?.[0]}
        value={formFields.email}
        onChange={createChangeHandler("email")}
        disabled={success}
        required
      />

      {success ? (
        <div className="rounded-xl bg-accentGreenTintBg px-4 py-3 text-center">
          <Text variant="bodySmBold" tone="dark">Reset link sent!</Text>
          <Text variant="bodySm" tone="body" className="mt-0.5">
            Please check your email for the reset link.
          </Text>
        </div>
      ) : (
        <>
          {couldNotBeSent && (
            <Text variant="bodySm" tone="inherit" className="text-center !text-accentError">
              Could not send reset link. Please check your email address.
            </Text>
          )}
          <Button variant="primary" fullWidth type="submit" disabled={isLoading}>
            {isLoading ? "Sending…" : "Continue"}
          </Button>
        </>
      )}
    </form>
  );
}
